import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

const isEmptyParagraph = (node: ADFEntity): boolean =>
	Boolean(node.type === 'paragraph' && node.content && node.content.length === 0);

/**
 * Editor produces inconsistent values for empty ADF that can either be:
 * 1. empty content array
 * 2. content array with a single empty paragraph object
 *
 * Even though using a single format (1) has been decided and changes rolled out,
 * nobody fixed historical data we have in all the databases there are,
 * so we need to have this utility function around.
 *
 * See: https://product-fabric.atlassian.net/wiki/spaces/E/pages/881362244/ADF+Change+42+Uniform+Empty+ADF+Representation
 * See: https://product-fabric.atlassian.net/browse/EX-1186
 * See also: https://product-fabric.atlassian.net/browse/EX-1286
 * See: https://app.launchdarkly.com/jira/production/features/EX-759-use-only-one-format-for-empty-adf/targeting
 */
export const isAdfEmpty = (adf: ADFEntity): boolean => {
	if (adf.content == null) {
		return true;
	}
	const firstNode = adf.content[0];
	if (firstNode == null) {
		return true;
	}
	return adf.content.length === 1 && isEmptyParagraph(firstNode);
};

export const removeAccessLevel = (adf: ADFEntity): ADFEntity | false =>
	traverse(adf, {
		mention: (node: ADFEntity) => {
			// Omit accessLevel as documents from the API do not include this property and we need dirty checks to be correct
			// @ts-expect-error - TS2339 - Property 'accessLevel' does not exist on type '{ [name: string]: any; } | undefined'.
			const { accessLevel, ...attrs } = node.attrs;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return {
				...node,
				attrs,
			} as ADFEntity;
		},
	});
